import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "zudoku/ui/Card";
import { Button } from "zudoku/ui/Button";
import { Lock, ClipboardList, CheckCircle, Hospital, Rocket, LibraryBig, ScrollText, Route } from "zudoku/icons";

const HomePage = () => {
  const apiFeatures = [
    {
      title: "Authentication",
      description:
        "Secure API authentication for Health Information Exchange with OAuth 2.0",
      icon: <Lock size={32} />,
      href: "/auth/authentication", //Tenmporary fix to 403 errors (But should be /auth)
    },
    {
      title: "Claims & Preauths",
      description:
        "Comprehensive eClaims and preauthorization workflows for healthcare providers",
      icon: <ClipboardList size={32} />,
      href: "/eclaims/eligibility", //Tenmporary fix to 403 errors (But should be /eclaims)
    },
    {
      title: "Consent Services",
      description:
        "Manage patient consent and data privacy compliance in healthcare",
      icon: <CheckCircle size={32} />,
      href: "/consent/start-visit-consent", //Tenmporary fix to 403 errors (But should be /consent)
    },
    {
      title: "HIE Registries",
      description:
        "Access national health registries and patient information systems",
      icon: <Hospital size={32} />,
      href: "/registries/client-registry", //Tenmporary fix to 403 errors (But should be /registries)
    },
  ];

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6">
          Health Information Exchange API
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          Comprehensive API documentation for healthcare integrators and
          providers to integrate with national health systems efficiently and
          securely
        </p>

        {/* SVG Doodle */}
        <div className="mb-8 flex justify-center">
          <svg
            width="240"
            height="240"
            viewBox="0 0 240 240"
            className="text-primary"
          >
            {/* Medical Cross */}
            <rect
              x="110"
              y="80"
              width="20"
              height="80"
              rx="2"
              fill="currentColor"
              opacity="0.2"
            />
            <rect
              x="80"
              y="110"
              width="80"
              height="20"
              rx="2"
              fill="currentColor"
              opacity="0.2"
            />

            {/* API Connection Lines */}
            <circle
              cx="60"
              cy="60"
              r="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              opacity="0.4"
            />
            <circle
              cx="180"
              cy="60"
              r="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              opacity="0.4"
            />
            <circle
              cx="60"
              cy="180"
              r="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              opacity="0.4"
            />
            <circle
              cx="180"
              cy="180"
              r="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              opacity="0.4"
            />

            {/* Connection lines to center */}
            <line
              x1="72"
              y1="60"
              x2="100"
              y2="90"
              stroke="currentColor"
              strokeWidth="2"
              opacity="0.3"
              strokeDasharray="4,4"
            />
            <line
              x1="168"
              y1="60"
              x2="140"
              y2="90"
              stroke="currentColor"
              strokeWidth="2"
              opacity="0.3"
              strokeDasharray="4,4"
            />
            <line
              x1="72"
              y1="180"
              x2="100"
              y2="150"
              stroke="currentColor"
              strokeWidth="2"
              opacity="0.3"
              strokeDasharray="4,4"
            />
            <line
              x1="168"
              y1="180"
              x2="140"
              y2="150"
              stroke="currentColor"
              strokeWidth="2"
              opacity="0.3"
              strokeDasharray="4,4"
            />

            {/* Data nodes */}
            <circle cx="60" cy="60" r="4" fill="currentColor" />
            <circle cx="180" cy="60" r="4" fill="currentColor" />
            <circle cx="60" cy="180" r="4" fill="currentColor" />
            <circle cx="180" cy="180" r="4" fill="currentColor" />
            <circle cx="120" cy="120" r="6" fill="currentColor" />
          </svg>
        </div>

        <Button size="lg" asChild>
          <a href="/introduction">Get Started</a>
        </Button>
      </section>

      {/* API Features Cards */}
      <section>
        <h2 className="text-3xl font-bold text-center mb-12">API Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {apiFeatures.map((feature) => (
            <Card
              key={feature.title}
              className="hover:shadow-lg transition-shadow"
            >
              <a href={feature.href} className="no-underline block h-full">
                <CardHeader>
                  <div className="text-4xl mb-2">{feature.icon}</div>
                  <CardTitle>{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <span className="text-sm text-primary hover:underline">
                    Explore API →
                  </span>
                </CardContent>
              </a>
            </Card>
          ))}
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="mt-16">
        <h2 className="text-3xl font-bold text-center mb-8">Quick Links</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <Card className="hover:shadow-md transition-shadow">
            <a href="/catalog" className="no-underline block">
              <CardHeader>
                <CardTitle className="text-lg"> <LibraryBig size={32}></LibraryBig> API Catalog</CardTitle>
                <CardDescription>Browse all available APIs</CardDescription>
              </CardHeader>
            </a>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <a href="/docs/userJourney" className="no-underline block">
              <CardHeader>
                <CardTitle className="text-lg"> <ScrollText size={32}></ScrollText> Integration User Journey</CardTitle>
                <CardDescription>
                  Steps to integrate with the HIE APIs
                </CardDescription>
              </CardHeader>
            </a>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <a href="/docs/goLive" className="no-underline block">
              <CardHeader>
                <CardTitle className="text-lg"> <Rocket size={32}></Rocket> Go Live Process</CardTitle>
                <CardDescription>
                  How to Go-Live with these APIs
                </CardDescription>
              </CardHeader>
            </a>
          </Card>
          <Card className="hover:shadow-md transition-shadow">
            <a href="/docs/scenarios/overview" className="no-underline block">
              <CardHeader>
                <CardTitle className="text-lg"> <Route size={32}></Route> Integration Scenarios</CardTitle>
                <CardDescription>
                  End-to-end API call sequences for each integration type
                </CardDescription>
              </CardHeader>
            </a>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
